<template>
  <div
    v-if="assignedCoach?.fullName"
    class="i-assigned-coach-details"
  >
    <div class="container">
      <div class="wrapper">
        <div class="grid-2col">
          <div class="left">
            <div class="top">
              <div
                v-if="'percentMatch' in assignedCoach"
                class="top-pick badge"
              >
                Top pick: {{ assignedCoach?.percentMatch }}% Match
              </div>
              <img
                class="image"
                :src="assignedCoach?.profilePhotoUrl || ''"
              />

              <template v-if="!!assignedCoach.introVideoUrl">
                <div
                  class="play-video"
                  @click="data.lightBoxVideo = assignedCoach.introVideoUrl || ''"
                >
                  <img
                    class="play"
                    :src="iconPlay"
                  />
                  <div class="time">{{ getVideoDuration(assignedCoach.introVideoUrl) }}</div>
                </div>
              </template>
            </div>

            <div
              v-if="isTabletOrLarger"
              class="ctas"
            >
              <iBaseButton
                v-if="!!assignedCoach?.fullName && !!assignedCoach?.calcomUsername"
                v-bind="BOOKING_BUTTON_STYLE"
                size="medium"
                :text="
                  userStore.isCoachingPaiduser
                    ? `Book a New Session with ${assignedCoachFirstName}`
                    : `Check ${assignedCoachFirstName}'s Availability`
                "
                @clicked="props.hasCustomOnClick ? $emit('ctaClicked') : openCalcomModal(assignedCoach?.calcomUsername)"
              />
              <template v-if="userStore.isCoachingPaiduser">
                <iBaseButton
                  v-if="userStore.getCoachingStatus != CoachingStatus.ONCE"
                  v-bind="OTHER_BUTTON_STYLE"
                  text="Manage Subscription"
                  @clicked="openUrl(SUBSCRIPTION_LINK)"
                />
                <iBaseButton
                  v-if="!userStore.isReportUser"
                  v-bind="OTHER_BUTTON_STYLE"
                  text="Take a Career Test"
                  @clicked="clickOnTakeTest"
                />
              </template>
            </div>
          </div>

          <div class="right">
            <div class="coach">{{ assignedCoach?.fullName || "Your Coach" }} <img :src="iconCheckBlue" /></div>
            <div class="text">Career Coach</div>
            <div class="text rating">
              <img :src="iconStars1" />
              {{ assignedCoach?.rating }} stars
              <span
                class="review-cta"
                @click="data.showReviewModal = true"
                >(<u>{{ numberOfReviews }} reviews</u>)</span
              >
            </div>
            <div
              v-if="!isTabletOrLarger && !hideMobileCtas"
              class="ctas"
            >
              <iBaseButton
                v-if="!!assignedCoach?.fullName && !!assignedCoach?.calcomUsername"
                v-bind="BOOKING_BUTTON_STYLE"
                :text="
                  userStore.isCoachingPaiduser
                    ? `Book a New Session with ${assignedCoachFirstName}`
                    : `Check ${assignedCoachFirstName}'s Availability`
                "
                @clicked="openCalcomModal(assignedCoach?.calcomUsername)"
              />
              <template v-if="userStore.isCoachingPaiduser">
                <iBaseButton
                  v-if="userStore.getCoachingStatus != CoachingStatus.ONCE"
                  v-bind="OTHER_BUTTON_STYLE"
                  text="Manage Subscription"
                  @clicked="openUrl(SUBSCRIPTION_LINK)"
                />
                <iBaseButton
                  v-if="!userStore.isReportUser"
                  v-bind="OTHER_BUTTON_STYLE"
                  text="Take a Career Test"
                  @clicked="clickOnTakeTest"
                />
              </template>
            </div>
            <div class="stats">
              <div class="stat">
                <img :src="iconBlueCircle" />
                <span
                  ><strong>{{ assignedCoach?.clientsServed || 280 }} clients</strong> landed their dream careers by
                  working with {{ assignedCoachFirstName || "this coach" }}.</span
                >
              </div>
              <div class="stat">
                <img :src="iconBlueCircle" />
                <span
                  >{{ assignedCoachFirstName || "this coach" }}’s clients achieved
                  <strong>{{ assignedCoach?.salaryBoostPercent || 30 }}%</strong> in average salary boost.</span
                >
              </div>
              <div class="stat">
                <img :src="iconBlueCircle" />
                <span
                  ><strong>99%</strong> of {{ assignedCoachFirstName || "this coach" }}’s clients received a job
                  interview within 3 months.</span
                >
              </div>
            </div>
            <div
              v-if="assignedCoachMatchReason"
              class="reason"
            >
              <p>
                <strong>Why are you matched with {{ assignedCoachFirstName || "this coach" }}?</strong>
              </p>
              <p>
                {{ assignedCoachMatchReason }}
              </p>
            </div>

            <div
              v-if="assignedCoach?.biography"
              class="biography"
            >
              <p v-if="assignedCoachMatchReason">
                <strong>{{ assignedCoachFirstName || "" }}'s Biography</strong>
              </p>
              <iBaseExpandableParagraph
                :text="assignedCoach?.biography || ''"
                text-size="unset"
                text-name="full biography"
                :collapsed-length="descriptionMaxlength"
              />
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="props.showHelp"
        class="help"
      >
        <div
          v-for="info in infoData.filter((e: any) => e.show)"
          :key="info.caption"
          class="option"
          @click="info.action()"
        >
          {{ info.title ?? info.caption }}
        </div>
      </div>
    </div>

    <template v-if="data.lightBoxVideo">
      <iBaseLightBox @close="data.lightBoxVideo = ''">
        <video
          :src="data.lightBoxVideo"
          autoplay
          controls
          class="light-box-video"
          type="video/mp4"
        ></video>
      </iBaseLightBox>
    </template>

    <iBaseModal
      class="embed"
      :visible="data.showCalcomEmbedModal"
      :fully-customisable="true"
      max-width="1120px"
      @click-on-close="data.showCalcomEmbedModal = false"
    >
      <div class="confirmation-modal">
        <iCalcomScheduler
          :on-booking-successful="goToSelectPlan"
          :embed-link="data.calcomEmbedLink"
          version="inline"
        />
      </div>
    </iBaseModal>

    <iBaseModal
      :visible="data.showReviewModal"
      :fully-customisable="true"
      :enable-bg-close="true"
      max-width="840px"
      @click-on-close="data.showReviewModal = false"
    >
      <div class="review-modal">
        <Carousel
          :autoplay="30000"
          :items-to-scroll="1"
          :items-to-show="isDesktopOrLarger ? 2 : 1"
          class="reviews-carousel"
          pause-autoplay-on-hover
          wrap-around
          snap-align="start"
        >
          <Slide
            v-for="(review, index) in reviewData"
            :key="index"
          >
            <div class="review">
              <div class="info">
                <img
                  class="rev-image"
                  :src="review.image"
                /><strong>{{ review.name }}</strong>
              </div>
              <div
                class="progress"
                v-html="review.progress"
              ></div>
              <img
                class="stars"
                :src="iconStars"
              />
              <div class="comment">{{ review.comment }}</div>
            </div>
          </Slide>
          <template #addons>
            <navigation>
              <template #next>
                <span>
                  <img
                    :src="iconArrowRightGray"
                    alt="next-icon"
                  />
                </span>
              </template>
              <template #prev>
                <span>
                  <img
                    :src="iconArrowLeftGray"
                    alt="prev-icon"
                  />
                </span>
              </template>
            </navigation>
          </template>
        </Carousel>
      </div>
    </iBaseModal>

    <iBaseModal
      :visible="data.showInfoModal"
      :fully-customisable="true"
      :enable-bg-close="true"
      :max-width="isMobileOrLarger ? '600px' : '350px'"
      @click-on-close="data.showInfoModal = false"
    >
      <div class="info-modal">
        <div class="modal-top">
          <div class="caption">{{ infoData[data.activeInfoIndex].caption }}</div>
          <div v-html="infoData[data.activeInfoIndex].content"></div>
        </div>
        <div class="bottom">
          <iBaseButton
            v-bind="MODAL_BUTTON_STYLE"
            width="fit-content"
            text="Close"
            @clicked="data.showInfoModal = false"
          />
        </div>
      </div>
    </iBaseModal>
  </div>
</template>

<script setup lang="ts">
import { useCoachingStore } from "@/stores/coaching"
import { useUserStore } from "@/stores/user"
import { azureRadiance200, azureRadiance600, azureRadiance700, white } from "@/styles/colors"
import { breakpointLg, breakpointMd, breakpointSm, breakpointXl } from "@/styles/variables"
import { computed, onMounted, reactive } from "vue"
import { useRouter } from "vue-router"
import { Carousel, Navigation, Slide } from "vue3-carousel"

import { getVideoDuration } from "@/composables/getVideoDuration"
import useIsScreenSize from "@/composables/useIsScreenSize"
import useRandomNumber from "@/composables/useRandomNumber"

import { GlobalVariables } from "@/helpers/globalVariables"
import UrlService from "@/services/urlService"

import iBaseButton from "@/components/base/BaseButton.vue"
import iBaseExpandableParagraph from "@/components/base/BaseExpandableParagraph.vue"
import iBaseLightBox from "@/components/base/BaseLightBox.vue"
import iBaseModal from "@/components/base/BaseModal.vue"
import iCalcomScheduler from "@/components/global/CalcomScheduler.vue"

// ASSETS
import iconArrowLeftGray from "@/assets/icons/icon-arrow-left-gray.svg"
import iconArrowRightGray from "@/assets/icons/icon-arrow-right-gray.svg"
import iconBlueCircle from "@/assets/icons/icon-blue-circle.svg"
import iconStars1 from "@/assets/icons/icon-five-slashed-stars.svg"
import iconStars from "@/assets/icons/icon-five-stars.svg"
import iconPlay from "@/assets/icons/icon-play-blue-white.svg"
import iconCheckBlue from "@/assets/icons/icon-rounded-blue-tick.svg"
import imageBrenden from "@/assets/images/Brenden-trustpilot.png"
import imageChristine from "@/assets/images/Christine-trustpilot.png"
import imageCory from "@/assets/images/Cory-trustpilot.png"
import imageDaniel from "@/assets/images/Daniel-trustpilot.png"
import imageDavid from "@/assets/images/David-trustpilot.png"
import imageJulie from "@/assets/images/Julie-trustpilot.png"
import imageTristan from "@/assets/images/Tristan-trustpilot.png"

// TYPES
import CoachingStatus from "@/shared/enums/CoachingStatus"
import { CoachAssigned, CoachForSelection } from "@/shared/types/Coach"

// EMITS
defineEmits(["ctaClicked"])

// STORES AND ROUTES
const coachingStore = useCoachingStore()
const userStore = useUserStore()
const router = useRouter()

// COMPOSABLES
const isMobileOrLarger = useIsScreenSize(breakpointSm)
const isTabletOrLarger = useIsScreenSize(breakpointMd)
const isDesktopOrLarger = useIsScreenSize(breakpointLg)
const isMassiveOrLarger = useIsScreenSize(breakpointXl)
const numberOfReviews = useRandomNumber(userStore.getFullName || "Full Name", 130, 300)

// PROPS
const props = defineProps({
  coach: {
    type: Object as () => CoachAssigned | CoachForSelection | null,
    default: null
  },
  showHelp: {
    type: Boolean,
    default: true
  },
  hasCustomOnClick: {
    type: Boolean,
    default: false
  },
  hideMobileCtas: {
    type: Boolean,
    default: false
  }
})

// CONSTANTS
const BOOKING_BUTTON_STYLE = {
  backgroundColor: azureRadiance700,
  borderRadius: "8px",
  fontWeight: "500",
  textColor: white,
  width: "100%",
  padding: "16px 24px",
  size: "medium",
  pulseColor: azureRadiance200
}
const OTHER_BUTTON_STYLE = {
  backgroundColor: "transparent",
  borderRadius: "8px",
  fontWeight: "500",
  textColor: azureRadiance700,
  width: "100%",
  padding: "16px 24px",
  version: "outline"
}
const MODAL_BUTTON_STYLE = {
  size: "small",
  fontWeight: "500",
  width: "100%",
  backgroundColor: azureRadiance600,
  borderRadius: "12px",
  textColor: white
}
const SUBSCRIPTION_LINK = "https://billing.stripe.com/p/login/eVa4hK1JEcw43g4aEE"

// DATA
const data = reactive({
  lightBoxVideo: "",
  showCalcomEmbedModal: false,
  calcomEmbedLink: "",
  showReviewModal: false,
  showInfoModal: false,
  activeInfoIndex: 0
})

// COMPUTED
const assignedCoach = computed(() => (props.coach ? props.coach : coachingStore.getAssignedCoach))
const assignedCoachFirstName = computed(() => assignedCoach.value?.fullName?.split(" ")[0])
const assignedCoachMatchReason = computed(() => {
  const coach = assignedCoach.value
  if (!coach || !("matchReason" in coach)) return null
  return (coach as CoachAssigned).matchReason?.replace(/\*/g, "")
}) // Remove asterisks that might be added by the backend
const descriptionMaxlength = computed(() =>
  isMassiveOrLarger.value ? 810 : isDesktopOrLarger.value ? 550 : isMobileOrLarger.value ? 160 : 100
)
const reviewData = computed(() => {
  const coachFirstName = assignedCoach.value?.fullName?.split(" ")[0]
  return [
    {
      name: "Christine S.",
      image: imageChristine,
      progress: `Finding direction and <strong>confidence</strong> with ${coachFirstName}`,
      comment: `I stumbled across Jobtest during my Google research while contemplating a career shift. After taking the career test, I signed up for biweekly sessions with ${coachFirstName}. Beyond just providing accountability, ${coachFirstName} guided me to online and local resources I wasn't aware of and gave me the confidence to pursue a career in interior design. ${coachFirstName} is incredibly kind and supportive, celebrating my progress each time we meet. I’ve gained so much direction and stability, and I hope my future job suits me as well as ${coachFirstName}'s does to them!`
    },
    {
      name: "Brenden C.",
      image: imageBrenden,
      progress: `Pursuing a <strong>career change</strong> with ${coachFirstName}.`,
      comment: `I was looking for a career change, and Jobtest really helped me clarify my career interests better than any other test. The offer to speak with ${coachFirstName} was super helpful. ${coachFirstName} was extremely friendly and insightful, guiding me on the next steps I should take. I now feel more confident with a guided plan to achieve my goals.`
    },
    {
      name: "Julie B.",
      image: imageJulie,
      progress: `<strong>Transitioning to a new career</strong> with ${coachFirstName}.`,
      comment: `I had the good fortune of meeting with ${coachFirstName}, who really understands what it's like to make a career transition. ${coachFirstName} asked great questions, offered valuable insights, and provided actionable next steps. As a former teacher like me, ${coachFirstName} brought both optimism and expertise to our sessions. I'm now feeling much more hopeful about my career journey.`
    },
    {
      name: "Tristan B.",
      image: imageTristan,
      progress: `<strong>Moving forward</strong> with ${coachFirstName}.`,
      comment: `I was honestly apprehensive about trying this; it could very easily be a scam. But after taking the assessment, I got paired with ${coachFirstName}, who truly listened to me, and we made a great connection right away. ${coachFirstName} introduced me to many things, including positions and industries I had never even heard of. I’m quite excited to see what the future holds, and I think Jobtest is helping me move in the right direction.`
    },
    {
      name: "Daniel B.",
      image: imageDaniel,
      progress: `Exploring <strong>new career paths</strong> with ${coachFirstName}.`,
      comment: `Today, ${coachFirstName} was incredibly thorough and positive, helping me navigate the results from jobtest.org and offering valuable insights into potential careers. ${coachFirstName}'s dedication made me feel truly valued, and our conversation felt like a discussion between good friends, backed by accurate test data. I’m grateful for the time and extra effort ${coachFirstName} put in to help me explore new perspectives and potential roles.`
    },
    {
      name: "Cory O.",
      image: imageCory,
      progress: `<strong>Rediscovering purpose</strong> with ${coachFirstName}`,
      comment: `Jobtest uses a unique scoring mechanism that opened my eyes to careers I hadn't considered before. I chose the coaching option, and ${coachFirstName} helped me think deeply about careers, applications, resumes, and the job market in a whole new way. Thanks to ${coachFirstName} and Jobtest, I'm now on a journey to find work that inspires me, has a real impact, and aligns with who I am as a person. This has been a powerful experience in my mid-30s, realizing that monetary success alone isn't enough.`
    },
    {
      name: "David F.",
      image: imageDavid,
      progress: `From skepticism to <strong>success</strong> with ${coachFirstName}`,
      comment: `I was skeptical at first, but ${coachFirstName} changed my mind almost immediately after our first conversation. I was just expecting some quick resume tips, but ${coachFirstName} ended up coaching me for a couple of months, meeting every other week. Ultimately, it's what you make of it, but I’d say ${coachFirstName} is one of the best coaches in the game.`
    }
  ]
})
const infoData = computed(() => {
  return [
    {
      caption: "How much is the coaching session?",
      show: !userStore.isCoachingPaiduser,
      action: () => showInfoModal(0),
      content:
        "Our coaching sessions are designed to be flexible and tailored to your needs. You can start with a single 30-minute discovery session and then opt for a more frequent schedule with weekly sessions. <strong>Once you've selected the time and date for your first session</strong>, you'll have the opportunity to customize your coaching plan to best suit your goals.<br/><br/>All of our coaches at jobtest.org offer their expertise at a consistent rate. Depending on the plan you choose, <strong>pricing ranges from $149.90 for a 30-minute discovery session to $349.90 for a weekly session plan</strong>."
    },
    {
      title: "I am not ready to make a decision yet.",
      caption: "Not ready to make a decision yet?",
      show: !userStore.isCoachingPaiduser,
      action: () => showInfoModal(1),
      content:
        "We completely understand that making a decision about coaching is a big step. It’s important to feel confident and informed before committing. Here are a few things that might help you:<br/><br/><strong>Discovery Session:</strong> Consider starting with a no-obligation 30-minute discovery session. This session is designed to give you a firsthand experience of what our coaching can offer, helping you decide if it’s the right fit for you.<br/><br/><strong>Flexible Plans:</strong>  Our coaching plans are flexible and customizable. Whether you're ready to dive in or just want to start with a single session, you can choose a plan that matches your comfort level.<br/><br/><strong>100% Money-Back Guarantee:</strong>  If you're still unsure, remember that we offer a 100% money-back guarantee. This means you can try out our coaching services risk-free. Please read our <a href='https://www.jobtest.org/refund-policy' target='_blank'>Refund Policy</a> for more details."
    },
    {
      caption: "I want to change my coach.",
      show: userStore.isCoachingPaiduser,
      action: () => openUrl("https://survey.zigpoll.com/2tVLrZ1upJBnDCLQ3/2worxsN49rTtX4Brd")
    },
    {
      caption: "Can I book a single session with my coach?",
      show: userStore.isCoachingPaiduser,
      action: () => showInfoModal(3),
      content: `Yes! You can book a 30-min on-demand session with your coach without needing to subscribe. Please choose a single session option in your checkout page after selecting your desired time and date from ${assignedCoachFirstName.value}'s calendar.`
    },
    {
      title: userStore.isCoachingPaiduser
        ? "My coach doesn't have any availability."
        : "I can’t find a coach with the right availability.",
      caption: userStore.isCoachingPaiduser
        ? "Your coach doesn't have any availability?"
        : "Can’t find a coach with the right availability?",
      show: true,
      action: () => showInfoModal(4),
      content:
        "We understand how important it is to find a coach who fits your schedule. Our top coaches are in high demand, but we’re committed to helping you find the right match. Here’s what you can do:<br/><br/><strong>Join the Waitlist:</strong> If your preferred coach isn’t available, you can <strong><a href='https://survey.zigpoll.com/2tVLrZ1upJBnDCLQ3/2wUgT342Yyt8GCi7d' target='_blank'>join their waitlist</a></strong>. We’ll notify you as soon as a spot opens up that fits your schedule.<br/><br/><strong>Personal Assistance:</strong> If you’re having trouble finding availability, our team is here to help. Please <strong><a href='https://survey.zigpoll.com/2tVLrZ1upJBnDCLQ3/2wUhqYcrMubg4Wmxw' target='_blank'>fill out this form</a></strong>, and a specialist will reach out to work with you personally to secure a session time that fits your schedule."
    }
  ]
})

// FUNCTIONS
function clickOnTakeTest() {
  router.push({ name: GlobalVariables.urls.report.children.testRetake.routeName })
}

function openUrl(redirectUrl: string) {
  window.open(redirectUrl, "_blank")
}

function openCalcomModal(calcomUsername: string | null | undefined) {
  if (!calcomUsername) return

  const isUnpurchasedExecutivePotential = !userStore.isCoachingHasEngaged && userStore.isExecutiveCoachingPotential
  const slug = isUnpurchasedExecutivePotential ? "discovery-60" : "discovery-30"
  data.calcomEmbedLink = UrlService.generateCalComEmbedLink(`${calcomUsername}/${slug}`)
  data.showCalcomEmbedModal = true
}

const goToSelectPlan = (e: any) => {
  setTimeout(() => {
    router.push({
      name: userStore.isExecutiveCoachingPotential
        ? GlobalVariables.urls.coaching.children.choosePlan.routeName
        : GlobalVariables.urls.coaching.children.selectPlan.routeName,
      query: { date: e.detail.data.date }
    })
  }, 1700)
}

const showInfoModal = (index: number) => {
  data.activeInfoIndex = index
  data.showInfoModal = true
}

// LIFECYCLE HOOKS
onMounted(async () => {
  if (!coachingStore.hasAssignedCoach) await coachingStore.loadAssignedCoach()
})
</script>

<style lang="scss" scoped>
.i-assigned-coach-details {
  background-color: $black50;

  .badge {
    padding: 4px 12px;
    color: white;
    font-weight: 500;
    background-image: $blue-purple-gradient;

    @include border-radius-xxl;

    width: fit-content;

    @include text-sm;
  }

  .ctas {
    @include flex(column, start, center, $gap: 10px);

    width: 100%;
  }

  .container {
    @include flex($direction: column, $justify: center, $align: center, $gap: 20px);

    margin: auto;
    width: 100%;
    padding: 30px 16px 15px;

    @media screen and (min-width: $breakpoint-md) {
      padding: 30px 24px;
    }

    @media screen and (min-width: $breakpoint-lg) {
      max-width: 1280px;
    }

    .wrapper {
      background-color: $white;
      border-radius: 22px;
      padding: 15px;

      @media screen and (min-width: $breakpoint-md) {
        padding: 30px;
      }

      .grid-2col {
        @include flex($direction: column, $justify: start, $align: start, $gap: 15px);

        @media screen and (min-width: $breakpoint-md) {
          flex-direction: row;
          gap: 32px;
        }

        .left {
          width: 100%;
          align-self: center;
          position: relative;

          @include flex(column, start, center, $gap: 20px);

          @media screen and (min-width: $breakpoint-md) {
            position: sticky;
            top: 20px;
            align-self: unset;
            flex: 0 0 300px;
          }

          @media screen and (min-width: $breakpoint-lg) {
            flex: 0 0 400px;
          }

          .top {
            position: relative;

            .image {
              width: 100%;
              height: auto;
              border-radius: 8px;
            }

            .top-pick {
              position: absolute;
              right: 12px;
              top: 12px;
              text-transform: uppercase;

              @include text-xs;

              @media screen and (min-width: $breakpoint-sm) {
                @include text-sm;

                font-weight: 400;
              }
            }

            .play-video {
              position: absolute;
              cursor: pointer;
              left: 8px;
              bottom: 8px;

              @include border-radius-xxl;

              background-color: rgb(159 159 159 / 50%);

              @include flex(row, start, center, $gap: 8px);

              padding-right: 10px;

              .play {
                width: 32px;
                height: 32px;
              }

              .time {
                color: $white;

                @include text-sm;

                font-weight: 500;
                padding: 6px 0;
              }
            }
          }
        }

        .right {
          .text {
            @include text-sm;

            font-weight: 500;
            margin-bottom: 10px;
            color: $black700;

            @media screen and (min-width: $breakpoint-sm) {
              @include text-md;
            }
          }

          .coach {
            @include text-lg;

            font-weight: 700;
            color: $black950;

            @include flex(row, space-between, center, $gap: 5px);

            @media screen and (min-width: $breakpoint-sm) {
              @include display-md;

              margin-bottom: 6px;
            }

            @media screen and (min-width: $breakpoint-md) {
              @include flex(row, start, center, $gap: 25px);
            }
          }

          .rating {
            color: $bilbao500;

            @media screen and (min-width: $breakpoint-sm) {
              margin-bottom: 5px;
            }

            img {
              vertical-align: text-bottom;
            }

            .review-cta {
              cursor: pointer;
            }
          }

          .stats {
            @include flex(column, start, start);

            width: fit-content;
            margin-bottom: 5px;

            .stat {
              @include flex(row, start, baseline, $gap: 12px);

              padding: 12px 0;
              width: 100%;

              &:not(:last-child) {
                border-bottom: solid 1px $zumthor100;
              }
            }
          }

          .biography,
          .reason {
            @include text-md;
          }

          .ctas {
            margin: 20px auto 0;
            max-width: 500px;
          }
        }
      }
    }

    .help {
      @include grid($cols: 1fr, $rows: 1fr 1fr 1fr, $align-items: stretch, $gap: 15px);

      width: 100%;
      margin-bottom: 5px;

      @media screen and (min-width: $breakpoint-md) {
        @include grid($cols: 1fr 1fr 1fr, $rows: 1fr, $align-items: stretch, $gap: 25px);
      }

      .option {
        @include flex($justify: center, $align: center);
        @include border-radius-xxl;

        background-color: $azure-radiance100;
        padding: 12px 20px;

        @include text-sm;

        font-weight: 400;
        color: $black950;
        text-align: center;
        cursor: pointer;

        @media screen and (min-width: $breakpoint-md) {
          @include text-md;
        }

        &:hover {
          background-color: $azure-radiance200;
        }
      }
    }
  }

  .light-box-video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    max-width: 840px;
    border-radius: 4px;
    overflow: hidden;
  }

  .embed {
    padding: 30px 0;

    @media screen and (min-width: $breakpoint-lg) {
      padding: unset;
    }

    .confirmation-modal {
      @include flex($direction: column, $justify: start, $align: center);

      margin: 45px 25px 5px;
    }
  }

  .review-modal {
    margin: 30px;

    .reviews-carousel {
      width: 100%;

      .review {
        @include flex($direction: column, $justify: start, $align: start, $gap: 5px);

        background-color: $black50;

        @include border-radius-sm;

        padding: 15px;
        width: 100%;
        height: 100%;
        margin: 0 8px;
        text-align: left;

        @media screen and (min-width: $breakpoint-sm) {
          padding: 22px 25px;

          @include border-radius-xl;
        }

        @media screen and (min-width: $breakpoint-lg) {
          width: 400px;
        }

        .info {
          @include text-md;

          font-weight: 400;

          > .rev-image {
            width: 32px;
            height: 32px;

            @include border-radius-rounded;

            margin-right: 10px;
            object-fit: cover;

            @media screen and (min-width: $breakpoint-sm) {
              width: 40px;
              height: 40px;
            }
          }
        }

        .progress {
          @include text-xs;

          font-weight: 400;

          @media screen and (min-width: $breakpoint-sm) {
            @include text-md;
          }
        }

        .stars {
          height: 18px;
        }

        .comment {
          @include text-sm;

          font-weight: 500;
          margin-top: 25px;

          @media screen and (min-width: $breakpoint-sm) {
            @include text-md;
          }
        }
      }
    }
  }

  .info-modal {
    .modal-top {
      @include flex($direction: column, $justify: start, $align: center, $gap: 17px);

      margin: 25px;

      @include text-md;

      @media screen and (min-width: $breakpoint-md) {
        margin: 35px;

        @include text-lg;
      }

      .caption {
        font-weight: 700;

        @include display-xs;

        text-align: center;

        @media screen and (min-width: $breakpoint-md) {
          @include display-sm;
        }
      }
    }

    .bottom {
      @include flex($direction: row, $justify: center, $align: center, $gap: 0);

      background-color: $black50;
      padding: 12px 25px;
      border-bottom-left-radius: 16px;
      border-bottom-right-radius: 16px;
    }
  }
}
</style>
<style lang="scss">
.i-select-coach {
  /* stylelint-disable */
  .carousel__next,
  .carousel__prev {
    margin: 0 -25px;
  }
  /* stylelint-enable */
}
</style>
